import { useMemo } from "react";

import type {
  AccommodationFacilityType,
  ArchitectureTypeSeries,
  ArchitectureTypeSize,
} from "../../../../../../__generated__/gql/graphql";
import type {
  AccommodationCount,
  FilteredArea,
} from "../../areas/_utils/area-filtering";
import { getAccommodationCountMap } from "../../areas/_utils/area-filtering";

/**
 * 部屋数集計hook
 *
 * エリアごとの部屋数と、フィルタ結果が空かどうかを算出する。
 */
export function useAccommodationCounts(
  data: {
    allAreas: FilteredArea[];
    displayAreas: FilteredArea[];
  },
  query: {
    totalOnly: boolean;
    hasAccommodationFilter: boolean;
    matchesAccommodationFilter: (accommodation: {
      facilityTypes: AccommodationFacilityType[];
      architectureType: {
        series: ArchitectureTypeSeries;
        seriesShortName: string;
        size?: ArchitectureTypeSize | null;
      };
    }) => boolean;
  },
): {
  isFilteredEmpty: boolean;
  accommodationCountMap: Map<string, AccommodationCount>;
} {
  const { allAreas, displayAreas } = data;
  const { totalOnly, hasAccommodationFilter, matchesAccommodationFilter } =
    query;

  // エリアごとの部屋数（バッジ表示用）
  // total: allAreas（フィルター未適用）から算出
  // available: CICO + フィルター適用後のdisplayAreasから算出
  const accommodationCountMap = useMemo(() => {
    if (!hasAccommodationFilter) {
      return getAccommodationCountMap(allAreas, displayAreas, totalOnly);
    }

    // NOTE: hasAccommodationFilterがtrueの場合の処理が複雑になっているが、
    // コンポーネントの構造や役割を見直せばシンプルにできる予定なので、
    // 一旦愚直な実装のまま置いておく。
    const filtered = displayAreas
      .map((area) => ({
        ...area,
        sites: area.sites
          .map((site) => ({
            ...site,
            accommodations: site.accommodations.filter(
              matchesAccommodationFilter,
            ),
          }))
          .filter((site) => site.accommodations.length > 0),
      }))
      .filter((area) => area.sites.length > 0);
    return getAccommodationCountMap(allAreas, filtered, totalOnly);
  }, [
    allAreas,
    displayAreas,
    totalOnly,
    hasAccommodationFilter,
    matchesAccommodationFilter,
  ]);

  // フィルター適用後に表示対象の部屋が0件か
  const isFilteredEmpty = useMemo(() => {
    if (!hasAccommodationFilter) return false;
    return Array.from(accommodationCountMap.values()).every(
      (rc) => (rc.available ?? 0) === 0,
    );
  }, [hasAccommodationFilter, accommodationCountMap]);

  return { isFilteredEmpty, accommodationCountMap };
}
